"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo } from "react";
import { usePathname } from "next/navigation";
import { resolveRouteKey } from "@/shared/schema/feature-flags";
import { getAddonDefinition } from "@/domain/addon";
import { isAddonFeature } from "@/domain/features";
import { useAnnouncementStore } from "./use-announcement-store";
import { useActiveAnnouncement } from "./use-active-announcement";
import { useGlobalAnnouncement } from "./use-global-announcement";
import { useMarkAnnouncementSeen } from "./use-mark-announcement-seen";
import { useIsFeedbackEnabled } from "./use-is-feedback-enabled";
import type { UseFeatureAnnouncement } from "./types";

// ============================================================================
// Public Hook
// ============================================================================

/**
 * Public hook for feature announcements
 *
 * Combines route detection, announcement data, and modal/sidebar state
 * into a single hook.
 *
 * Priority: unseen global announcement > feature-specific announcement.
 * Once the global announcement is dismissed, the feature-specific one takes over.
 *
 * Usage:
 * ```tsx
 * const { announcement, announcementType, openModal, dismissGlobal } = useFeatureAnnouncement()
 * ```
 */
export function useFeatureAnnouncement() {
  const $ = _c(35);
  const pathname = usePathname();
  let routeKey;
  let t0;
  if ($[0] !== pathname) {
    routeKey = resolveRouteKey(pathname);
    t0 = routeKey != null && isAddonFeature(routeKey);
    $[0] = pathname;
    $[1] = routeKey;
    $[2] = t0;
  } else {
    routeKey = $[1];
    t0 = $[2];
  }
  const isAddon = t0;
  let t1;
  bb0: {
    if (!isAddon || routeKey == null) {
      t1 = undefined;
      break bb0;
    }
    const t2 = routeKey as Parameters<typeof getAddonDefinition>[0];
    let t3;
    if ($[3] !== t2) {
      t3 = getAddonDefinition(t2);
      $[3] = t2;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    const def = t3;
    let t4;
    if ($[5] !== def) {
      t4 = def ? {
        from: def.accent.from,
        to: def.accent.to
      } : undefined;
      $[5] = def;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    t1 = t4;
  }
  const addonAccent = t1;
  const {
    data: featureAnnouncement
  } = useActiveAnnouncement(routeKey);
  const {
    data: globalAnnouncement,
    hasSeen: hasSeenGlobal
  } = useGlobalAnnouncement();
  const {
    markSeen
  } = useMarkAnnouncementSeen();
  const isGlobalDismissed = useAnnouncementStore(_temp);
  const showGlobal = globalAnnouncement != null && !hasSeenGlobal && !isGlobalDismissed;
  const activeAnnouncement = showGlobal ? globalAnnouncement : featureAnnouncement ?? undefined;
  const activeType = showGlobal ? "global" as const : "feature" as const;
  const feedbackKeyOverride = useAnnouncementStore(_temp2);
  const feedbackKey = feedbackKeyOverride ?? (activeType === "global" ? "platform" : routeKey);
  const isAnnouncementVisible = activeAnnouncement !== null && activeAnnouncement !== undefined;
  const {
    feedbackEnabled,
    feedbackDisplayMode,
    displayPosition
  } = useIsFeedbackEnabled(feedbackKey);
  const {
    isModalOpen,
    isSidebarOpen,
    openModal,
    closeModal,
    openSidebar,
    closeSidebar,
    dismissGlobal: dismissGlobalStore
  } = useAnnouncementStore();
  const globalAnnouncementId = globalAnnouncement?.id;
  let t2;
  if ($[7] !== dismissGlobalStore || $[8] !== globalAnnouncementId || $[9] !== markSeen) {
    t2 = () => {
      dismissGlobalStore();
      if (globalAnnouncementId) {
        markSeen(globalAnnouncementId).catch(_temp3);
      }
    };
    $[7] = dismissGlobalStore;
    $[8] = globalAnnouncementId;
    $[9] = markSeen;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const dismissGlobal = t2;
  let t3;
  if ($[11] !== closeModal || $[12] !== closeSidebar) {
    t3 = () => {
      closeSidebar();
      closeModal();
    };
    $[11] = closeModal;
    $[12] = closeSidebar;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== closeModal || $[15] !== closeSidebar || $[16] !== pathname) {
    t4 = [pathname, closeSidebar, closeModal];
    $[14] = closeModal;
    $[15] = closeSidebar;
    $[16] = pathname;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  useEffect(t3, t4);
  const t5 = activeAnnouncement ?? undefined;
  let t6;
  if ($[18] !== activeType || $[19] !== addonAccent || $[20] !== closeModal || $[21] !== closeSidebar || $[22] !== dismissGlobal || $[23] !== displayPosition || $[24] !== feedbackDisplayMode || $[25] !== feedbackEnabled || $[26] !== feedbackKey || $[27] !== isAddon || $[28] !== isAnnouncementVisible || $[29] !== isModalOpen || $[30] !== isSidebarOpen || $[31] !== openModal || $[32] !== openSidebar || $[33] !== t5) {
    t6 = {
      featureKey: feedbackKey,
      announcement: t5,
      announcementType: activeType,
      isAddonFeature: isAddon,
      addonAccent,
      feedbackEnabled,
      feedbackDisplayMode,
      displayPosition,
      isAnnouncementVisible,
      isModalOpen,
      isSidebarOpen,
      openModal,
      closeModal,
      openSidebar,
      closeSidebar,
      dismissGlobal
    };
    $[18] = activeType;
    $[19] = addonAccent;
    $[20] = closeModal;
    $[21] = closeSidebar;
    $[22] = dismissGlobal;
    $[23] = displayPosition;
    $[24] = feedbackDisplayMode;
    $[25] = feedbackEnabled;
    $[26] = feedbackKey;
    $[27] = isAddon;
    $[28] = isAnnouncementVisible;
    $[29] = isModalOpen;
    $[30] = isSidebarOpen;
    $[31] = openModal;
    $[32] = openSidebar;
    $[33] = t5;
    $[34] = t6;
  } else {
    t6 = $[34];
  }
  return t6;
}
function _temp3() {}
function _temp2(s_0) {
  return s_0.feedbackKeyOverride;
}
function _temp(s) {
  return s.isGlobalDismissed;
}